/**
 * Scales and tick arithmetic.
 *
 * Pure, and in a file of its own with no JSX, because `vitest.config.ts` runs
 * in a node environment: a test that renders a React chart would need jsdom,
 * which is a dependency this kit exists to avoid. The geometry is where the
 * bugs are anyway — a mis-scaled axis is a wrong chart, and a wrong chart is
 * worse than no chart because it is believed.
 *
 * The same split `features/reports/result-trend.logic.ts` already makes beside `TrendChart`.
 */

export interface LinearScale {
  (value: number): number;
  /** Pixels back to a value — the tooltip's "which point is under the cursor". */
  invert(px: number): number;
  ticks(count: number): number[];
  readonly domain: readonly [number, number];
  readonly range: readonly [number, number];
}

export interface BandScale {
  (key: string): number;
  /** The width of one band, after padding. */
  readonly bandwidth: number;
  /** Centre of a band — where a line's point or a label sits. */
  centre(key: string): number;
  /** Nearest key to a pixel position, for pointer tracking. */
  nearest(px: number): string | undefined;
  readonly keys: readonly string[];
}

/**
 * A linear map from a value domain to a pixel range.
 *
 * A flat domain (`min === max`) would divide by zero and collapse every point
 * onto one pixel. `Sparkline` and `TrendChart` each solve that inline with
 * `|| 1`, which pins a flat series to the baseline and reads as "zero". Solved
 * once here, and solved better: a flat series is drawn through the *middle* of
 * the plot, which is the honest picture of "nothing changed".
 */
export function linearScale(
  domain: readonly [number, number],
  range: readonly [number, number],
): LinearScale {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0;
  const flat = span === 0;

  const scale = ((value: number) =>
    flat ? (r0 + r1) / 2 : r0 + ((value - d0) / span) * (r1 - r0)) as {
    (value: number): number;
    invert(px: number): number;
    ticks(count: number): number[];
    domain: readonly [number, number];
    range: readonly [number, number];
  };

  scale.invert = (px: number) => (flat ? d0 : d0 + ((px - r0) / (r1 - r0)) * span);
  scale.ticks = (count: number) => ticksFor(d0, d1, count);
  scale.domain = domain;
  scale.range = range;

  return scale;
}

/**
 * Positions for a categorical axis.
 *
 * `padding` is the share of each step left blank, so 0.2 gives bars a fifth of
 * their slot as gutter. Keys are matched by value, not index, so a series with
 * a gap in it still lines up with the axis.
 */
export function bandScale(
  keys: readonly string[],
  range: readonly [number, number],
  padding = 0.2,
): BandScale {
  const [r0, r1] = range;
  const step = keys.length > 0 ? (r1 - r0) / keys.length : 0;
  const bandwidth = Math.max(0, step * (1 - padding));
  const offset = (step - bandwidth) / 2;
  const index = new Map(keys.map((key, i) => [key, i]));

  const scale = ((key: string) => {
    const i = index.get(key);
    return i === undefined ? Number.NaN : r0 + i * step + offset;
  }) as {
    (key: string): number;
    bandwidth: number;
    centre(key: string): number;
    nearest(px: number): string | undefined;
    keys: readonly string[];
  };

  scale.bandwidth = bandwidth;
  scale.centre = (key: string) => scale(key) + bandwidth / 2;
  scale.nearest = (px: number) => {
    if (keys.length === 0 || step === 0) return undefined;
    // Clamped rather than returning undefined outside the plot: a pointer a few
    // pixels past the last bar means "the last bar", not "nothing".
    const i = Math.min(keys.length - 1, Math.max(0, Math.floor((px - r0) / step)));
    return keys[i];
  };
  scale.keys = keys;

  return scale;
}

/**
 * Round a domain out to human numbers on a 1 / 2 / 5 × 10ⁿ ladder.
 *
 * So the axis reads 0, 25, 50, 75, 100 rather than 0, 23.4, 46.8 — and the top
 * gridline sits on a labelled tick instead of floating above the tallest bar.
 */
export function niceDomain(min: number, max: number, tickCount = 5): [number, number] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  if (min === max) {
    // A single repeated value still needs a band to sit in. Zero is the one
    // case where the honest picture is "nothing", not "nothing changed".
    return min === 0 ? [0, 1] : [Math.min(0, min), Math.max(0, max)];
  }

  const step = tickStep(min, max, tickCount);
  return [Math.floor(min / step) * step, Math.ceil(max / step) * step];
}

/** Tick values across a domain, inclusive of both rounded ends. */
export function ticksFor(min: number, max: number, count = 5): number[] {
  if (min === max || !Number.isFinite(min) || !Number.isFinite(max)) return [min];

  const step = tickStep(min, max, count);
  if (!Number.isFinite(step) || step <= 0) return [min, max];

  const first = Math.ceil(min / step);
  const last = Math.floor(max / step);
  // Generated from an index rather than by accumulating `value += step`.
  // Accumulating drifts — ten additions of 0.2 reach 0.6000000000000001, which
  // renders on the axis exactly as written.
  const places = decimalsFor(step);

  const out: number[] = [];
  for (let i = first; i <= last && out.length < 1000; i++) {
    out.push(Number((i * step).toFixed(places)));
  }
  return out;
}

function tickStep(min: number, max: number, count: number): number {
  const rough = (max - min) / Math.max(1, count);
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalised = rough / magnitude;

  // The 1/2/5 ladder is what makes an axis readable: those are the intervals
  // people can add up in their head.
  //
  // The thresholds are geometric means, not the ladder values themselves —
  // √50, √10, √2. Comparing against 5/2/1 rounds every step *up* to the next
  // rung, so a domain of 0-100 asked for five ticks would return a step of 50
  // and produce three.
  const nice =
    normalised >= Math.SQRT2 * 5
      ? 10
      : normalised >= Math.sqrt(10)
        ? 5
        : normalised >= Math.SQRT2
          ? 2
          : 1;
  return nice * magnitude;
}

/** How many decimal places a step needs, so `0.2` never prints as `0.2000…4`. */
function decimalsFor(step: number): number {
  return Math.max(0, Math.min(20, -Math.floor(Math.log10(step))));
}

/**
 * How many ticks a given pixel extent can carry.
 *
 * Density falls on narrow viewports rather than the labels shrinking, because
 * below about 10px axis text stops being readable and starts being texture.
 */
export function tickCountFor(extent: number, minSpacing = 56): number {
  return Math.max(2, Math.min(8, Math.floor(extent / minSpacing)));
}
